import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

// Make Pusher available globally
window.Pusher = Pusher

class EchoService {
    constructor() {
        this.echo = null
        this.isConnected = false
        this.tokenGetter = null
        this.allowedMatchChannelValidator = null
    }

    setAllowedMatchChannelValidator(validator) {
        this.allowedMatchChannelValidator = validator
    }

    async _authenticateChannel(socketId, channelName, callback) {
        if (channelName.startsWith('private-match.')) {
            const matchId = channelName.replace('private-match.', '')
            if (this.allowedMatchChannelValidator && !this.allowedMatchChannelValidator(matchId)) {
                callback(true, {
                    message: 'Not authorized to access this match channel',
                    status: 403,
                })
                return
            }
        }

        const token = this.tokenGetter ? this.tokenGetter() : null

        if (!token) {
            callback(true, { message: 'No authentication token available' })
            return
        }

        try {
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/broadcasting/auth`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    socket_id: socketId,
                    channel_name: channelName,
                }),
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({
                    message: 'Authentication failed',
                }))

                if (response.status === 403) {
                    console.warn(`Channel auth failed (403) for channel: ${channelName}`, errorData)
                } else {
                    console.error(
                        `Channel auth failed (${response.status}) for channel: ${channelName}`,
                        errorData,
                    )
                }

                callback(true, errorData)
            } else {
                const data = await response.json()
                callback(false, data)
            }
        } catch (error) {
            console.error('WebSocket auth error:', error)
            callback(true, {
                message: error.message || 'Authentication request failed',
            })
        }
    }

    init(tokenGetter = null) {
        if (this.echo) {
            return this.echo
        }

        this.tokenGetter = tokenGetter

        console.log('Initializing Echo with Pusher...')

        this.echo = new Echo({
            broadcaster: 'reverb',
            key: import.meta.env.VITE_REVERB_APP_KEY,
            wsHost: import.meta.env.VITE_REVERB_HOST,
            wsPort: import.meta.env.VITE_REVERB_PORT ?? 80,
            wssPort: import.meta.env.VITE_REVERB_PORT ?? 443,
            forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'https') === 'https',
            enabledTransports: ['ws', 'wss'],
            cluster: import.meta.env.VITE_REVERB_APP_CLUSTER,
            authEndpoint: `${import.meta.env.VITE_API_BASE_URL}broadcasting/auth`,
            authorizer: (channel) => ({
                authorize: (socketId, callback) => {
                    this._authenticateChannel(socketId, channel.name, callback)
                },
            }),
        })

        this.echo.connector.pusher.connection.bind('connected', () => {
            this.isConnected = true
            console.log('Echo connected')
        })

        this.echo.connector.pusher.connection.bind('disconnected', () => {
            this.isConnected = false
            console.log('Echo disconnected')
        })

        window.Echo = this.echo
        return this.echo
    }

    disconnect() {
        if (this.echo) {
            this.echo.disconnect()
            this.echo = null
            this.isConnected = false
            this.tokenGetter = null // Clear token getter on disconnect
            window.Echo = null
        }
    }

    get connected() {
        return this.isConnected
    }

    get instance() {
        return this.echo || this.init()
    }

    channel(name) {
        return this.instance.channel(name)
    }

    privateChannel(name) {
        return this.instance.private(name)
    }

    presenceChannel(name) {
        return this.instance.join(name)
    }

    leave(name) {
        return this.instance.leave(name)
    }

    leaveAll() {
        return this.instance.leaveAll()
    }

    unsubscribe(name) {
        if (!this.instance || !this.instance.connector?.pusher) {
            return
        }

        const pusher = this.instance.connector.pusher
        const channel = pusher.channel(name) || pusher.channel(`private-${name}`)

        if (channel) {
            channel.unsubscribe()
        }

        this.instance.leave(name)
    }
}

export default new EchoService()
